import React, { useEffect, useState } from "react";

/**
 * ObfuscatedEmail — renders an email address that is NOT present in the
 * initial server-rendered HTML. The `user` and `domain` props are kept
 * separate; the browser assembles the full address on mount via useEffect.
 *
 * Blocks the vast majority of plain-fetch scrapers and regex harvesters
 * that don't execute JavaScript. Does not defeat headless-browser
 * scrapers or humans inspecting the DOM — those aren't the target.
 *
 * Usage:
 *   <ObfuscatedEmail user="hello" domain="londonrenovationspecialists.com" />
 *   <ObfuscatedEmail user="hello" domain="londonrenovationspecialists.com" className="underline hover:text-terracotta" />
 */
export default function ObfuscatedEmail({ user, domain, className = "" }) {
    const [revealed, setRevealed] = useState(false);

    useEffect(() => {
        // Only reveal in a real browser environment. Guards against any
        // SSR-shaped rendering, and defers assembly until after paint so
        // the initial HTML snapshot never contains "user@domain".
        if (typeof document !== "undefined" && typeof window !== "undefined") {
            setRevealed(true);
        }
    }, []);

    if (!revealed) {
        // Placeholder — no valid parseable email in this snapshot.
        return (
            <span className={className} data-testid="obfuscated-email">
                [email&nbsp;protected]
            </span>
        );
    }

    const address = `${user}@${domain}`;
    return (
        <a
            href={`mailto:${address}`}
            className={className}
            data-testid="obfuscated-email"
        >
            {address}
        </a>
    );
}
